import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CaseStudies() {
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCaseStudies = async () => {
      try {
        const response = await axios.get(`${API}/case-studies`);
        setCaseStudies(response.data);
      } catch (error) {
        console.error("Error fetching case studies:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchCaseStudies();
  }, []);

  return (
    <div data-testid="case-studies-page" className="page-transition">
      {/* Hero Section */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-4">
              Case Studies
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-none text-slate-900 font-['Outfit'] mb-6">
              Client Success Stories
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed font-['Plus_Jakarta_Sans']">
              Explore how we've helped organizations across industries achieve their 
              technology goals and drive business transformation.
            </p>
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="bg-slate-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
              <p className="mt-4 text-slate-600">Loading case studies...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {caseStudies.map((study, index) => (
                <Link 
                  key={study.id}
                  to={`/case-studies/${study.slug}`}
                  data-testid={`case-study-card-${index}`}
                  className="group bg-white border border-slate-200 hover:border-blue-600 transition-colors"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img 
                      src={study.image_url}
                      alt={study.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-8">
                    <div className="flex items-center gap-4 mb-4">
                      <span className="text-xs font-bold uppercase tracking-wider text-blue-600">
                        {study.industry}
                      </span>
                      <span className="text-xs text-slate-400">|</span>
                      <span className="text-xs text-slate-500">
                        {study.client}
                      </span>
                    </div>
                    <h2 className="text-xl lg:text-2xl font-semibold text-slate-900 font-['Outfit'] tracking-tight mb-3 group-hover:text-blue-700 transition-colors">
                      {study.title}
                    </h2>
                    <p className="text-slate-600 text-sm leading-relaxed mb-4 font-['Plus_Jakarta_Sans'] line-clamp-2">
                      {study.challenge}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {study.services_used.slice(0, 3).map((service) => (
                        <span 
                          key={service}
                          className="px-3 py-1 bg-slate-100 text-slate-600 text-xs font-medium"
                        >
                          {service}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center text-blue-600 text-sm font-medium">
                      Read Case Study
                      <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-slate-900 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white font-['Outfit'] mb-4">
            Ready to Write Your Success Story?
          </h2>
          <p className="text-slate-400 mb-8 font-['Plus_Jakarta_Sans']">
            Let's discuss how we can help your organization achieve similar results.
          </p>
          <Link to="/contact">
            <Button 
              data-testid="case-studies-cta-button"
              className="bg-blue-700 hover:bg-blue-800 text-white rounded-none px-8"
            >
              Contact Us
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
